import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'app/components/ui/card';
import { Button } from 'app/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table';
import { Badge } from 'app/components/ui/badge';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import { useInventorySlice } from '../slice';
import { InventoryReportsProps, InventoryReportParams } from 'types/inventory';
import { BarChart3, Download, FileText, TrendingUp, AlertTriangle } from 'lucide-react';

const InventoryReports: React.FC<InventoryReportsProps> = ({ className }) => {
    const [reportParams, setReportParams] = useState<InventoryReportParams>({
        reportType: 'summary',
    });

    const { useGetInventoryReportsQuery } = useInventorySlice();
    const { data: report, isLoading, error } = useGetInventoryReportsQuery(reportParams);

    const handleReportTypeChange = (reportType: string) => {
        setReportParams(prev => ({ ...prev, reportType: reportType as any }));
    };

    const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
        setReportParams(prev => ({ ...prev, [field]: value }));
    };

    const handleFilterChange = (field: 'supplierId' | 'categoryId' | 'actionType', value: string) => {
        const numValue = value ? parseInt(value) : undefined;
        setReportParams(prev => ({
            ...prev,
            [field]: field === 'actionType' ? (value === 'all' ? undefined : value) : numValue
        }));
    };

    const renderSummaryReport = () => {
        if (!report?.data?.summary) return null;

        const { summary, by_supplier } = report.data;

        return (
            <div className="space-y-6">
                {/* Summary Statistics */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{summary.total_products}</div>
                            <p className="text-xs text-muted-foreground">
                                {summary.active_products} active
                            </p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium">In Stock</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-green-600">{summary.in_stock_products}</div>
                            <p className="text-xs text-muted-foreground">
                                {summary.out_of_stock_products} out of stock
                            </p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium">Total Stock</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{summary.total_stock}</div>
                            <p className="text-xs text-muted-foreground">
                                Avg: {summary.avg_stock_per_product} per product
                            </p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium">Inventory Value</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">${summary.total_inventory_value}</div>
                            <p className="text-xs text-muted-foreground">
                                Total value
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* By Supplier */}
                {by_supplier && by_supplier.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Inventory by Supplier</CardTitle>
                            <CardDescription>
                                Stock distribution across suppliers
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Supplier</TableHead>
                                        <TableHead>Products</TableHead>
                                        <TableHead>Total Stock</TableHead>
                                        <TableHead>Value</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {by_supplier.map((supplier: any, index: number) => (
                                        <TableRow key={index}>
                                            <TableCell className="font-medium">{supplier.supplier_name}</TableCell>
                                            <TableCell>{supplier.product_count}</TableCell>
                                            <TableCell>{supplier.total_stock}</TableCell>
                                            <TableCell>${supplier.inventory_value}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </div>
        );
    };

    const renderMovementsReport = () => {
        if (!report?.data?.movements) return null;

        const movements = report.data.movements;

        return (
            <Card>
                <CardHeader>
                    <CardTitle>Inventory Movements</CardTitle>
                    <CardDescription>
                        Detailed transaction history
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>Action</TableHead>
                                <TableHead>Change</TableHead>
                                <TableHead>Stock</TableHead>
                                <TableHead>Cost</TableHead>
                                <TableHead>Reference</TableHead>
                                <TableHead>Date</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {movements.map((movement: any) => (
                                <TableRow key={movement.log_id}>
                                    <TableCell className="font-medium">{movement.product_name}</TableCell>
                                    <TableCell>{movement.sku}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline">{movement.action_type}</Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={movement.quantity_change > 0 ? "default" : "secondary"}>
                                            {movement.quantity_change > 0 ? '+' : ''}{movement.quantity_change}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {movement.previous_stock} → {movement.new_stock}
                                    </TableCell>
                                    <TableCell>
                                        {movement.unit_cost ? `$${movement.unit_cost}` : '-'}
                                    </TableCell>
                                    <TableCell>{movement.reference_number || '-'}</TableCell>
                                    <TableCell>
                                        {new Date(movement.created_at).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    };

    const renderLowStockReport = () => {
        if (!report?.data?.low_stock_products) return null;

        const products = report.data.low_stock_products;

        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                        Low Stock Products
                    </CardTitle>
                    <CardDescription>
                        Products requiring immediate attention
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>Supplier</TableHead>
                                <TableHead>Stock</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {products.map((product: any) => (
                                <TableRow key={product.product_id}>
                                    <TableCell className="font-medium">{product.product_name}</TableCell>
                                    <TableCell>{product.sku}</TableCell>
                                    <TableCell>{product.supplier}</TableCell>
                                    <TableCell>
                                        <Badge variant={product.stock_quantity === 0 ? "destructive" : "secondary"}>
                                            {product.stock_quantity}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>${product.sale_price}</TableCell>
                                    <TableCell>
                                        <Badge variant={product.status === 1 ? "default" : "secondary"}>
                                            {product.status === 1 ? "Active" : "Inactive"}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    };

    const renderValueAnalysisReport = () => {
        if (!report?.data?.value_analysis) return null;

        const analysis = report.data.value_analysis;

        return (
            <Card>
                <CardHeader>
                    <CardTitle>Value Analysis</CardTitle>
                    <CardDescription>
                        Inventory value distribution
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Value Range</TableHead>
                                <TableHead>Products</TableHead>
                                <TableHead>Total Stock</TableHead>
                                <TableHead>Total Value</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {analysis.map((item: any, index: number) => (
                                <TableRow key={index}>
                                    <TableCell className="font-medium">{item.value_range}</TableCell>
                                    <TableCell>{item.product_count}</TableCell>
                                    <TableCell>{item.total_stock}</TableCell>
                                    <TableCell>${item.total_value}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    };

    const renderReportContent = () => {
        switch (reportParams.reportType) {
            case 'summary':
                return renderSummaryReport();
            case 'movements':
                return renderMovementsReport();
            case 'low_stock':
                return renderLowStockReport();
            case 'value_analysis':
                return renderValueAnalysisReport();
            default:
                return null;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                    Failed to load inventory reports. Please try again later.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className={`space-y-6 ${className}`}>
            {/* Report Controls */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Report Controls
                    </CardTitle>
                    <CardDescription>
                        Configure and generate inventory reports
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {/* Report Type */}
                        <div className="space-y-2">
                            <Label htmlFor="reportType">Report Type</Label>
                            <Select value={reportParams.reportType} onValueChange={handleReportTypeChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select report type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="summary">
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4" />
                                            <span>Summary</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="movements">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp className="h-4 w-4" />
                                            <span>Movements</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="low_stock">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4" />
                                            <span>Low Stock</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="value_analysis">
                                        <div className="flex items-center gap-2">
                                            <BarChart3 className="h-4 w-4" />
                                            <span>Value Analysis</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Date Range */}
                        <div className="space-y-2">
                            <Label htmlFor="startDate">Start Date</Label>
                            <Input
                                id="startDate"
                                type="date"
                                value={reportParams.startDate || ''}
                                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="endDate">End Date</Label>
                            <Input
                                id="endDate"
                                type="date"
                                value={reportParams.endDate || ''}
                                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                            />
                        </div>

                        {/* Action Type Filter */}
                        <div className="space-y-2">
                            <Label htmlFor="actionType">Action Type</Label>
                            <Select
                                value={reportParams.actionType || 'all'}
                                onValueChange={(value) => handleFilterChange('actionType', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All actions" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Actions</SelectItem>
                                    <SelectItem value="adjustment">Adjustment</SelectItem>
                                    <SelectItem value="sale">Sale</SelectItem>
                                    <SelectItem value="return">Return</SelectItem>
                                    <SelectItem value="damage">Damage</SelectItem>
                                    <SelectItem value="transfer">Transfer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline">
                            <Download className="h-4 w-4 mr-2" />
                            Export Report
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Report Content */}
            {renderReportContent()}
        </div>
    );
};

export default InventoryReports;
